import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaSave, FaPlus, FaTrash, FaSpinner, FaImage, FaTimes, FaUpload } from 'react-icons/fa';
import { usePortfolio } from '../../context/PortfolioContext';
import { uploadAPI } from '../../utils/api.js';

const DEFAULT_ROLES = ["AI Engineer", "Freelancer", "YouTuber", "Innovator"];

const EditHero = ({ profileData, setProfileData, handleProfileChange, saving, setHasChanges }) => {
  const [imageUploading, setImageUploading] = useState(false);
  const [roleInput, setRoleInput] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const MAX_IMAGE_SIZE_MB = 20;
  const { updatePortfolioData, portfolioData } = usePortfolio();

  // Sync form state with context/profile changes
  useEffect(() => {
    if (portfolioData && portfolioData.profile) {
      setProfileData({ ...portfolioData.profile });
    }
  }, [portfolioData, setProfileData]);

  const handleImageUpload = async (file) => {
    if (!file) return;
    setImageUploading(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      if (response.success) {
        setProfileData(prev => ({ ...prev, image_url: response.data.url }));
        setHasChanges && setHasChanges(true);
      }
    } catch {
      // error toast can be added
    } finally {
      setImageUploading(false);
    }
  };

  const handleAddRole = async () => {
    if (roleInput.trim()) {
      const updatedProfile = { ...profileData, roles: [...(profileData.roles || []), roleInput.trim()] };
      setProfileData(updatedProfile);
      try {
        await updatePortfolioData('profile', updatedProfile);
        toast.success('Role added and saved!');
      } catch (err) {
        toast.error('Failed to add role: ' + (err.message || err));
      }
      setRoleInput('');
    }
  };
  const handleEditRole = async (idx, value) => {
    const newRoles = [...(profileData.roles || [])];
    newRoles[idx] = value;
    const updatedProfile = { ...profileData, roles: newRoles };
    setProfileData(updatedProfile);
    try {
      await updatePortfolioData('profile', updatedProfile);
      toast.success('Role updated and saved!');
    } catch (err) {
      toast.error('Failed to update role: ' + (err.message || err));
    }
  };
  const handleRemoveRole = async (idx) => {
    const newRoles = (profileData.roles || []).filter((_, i) => i !== idx);
    const updatedProfile = { ...profileData, roles: newRoles };
    setProfileData(updatedProfile);
    try {
      await updatePortfolioData('profile', updatedProfile);
      toast.success('Role removed and saved!');
    } catch (err) {
      toast.error('Failed to remove role: ' + (err.message || err));
    }
  };

  if (!profileData) return <div className="text-gray-400">Loading...</div>;

  return (
    <div className="max-w-xl mx-auto bg-white/5 p-8 rounded-2xl shadow-lg border border-gray-700">
      <h3 className="text-2xl font-bold text-primary mb-6 text-center">Profile Editor</h3>
      <div className="flex flex-col items-center mb-6">
        {profileData.image_url ? (
          <img src={profileData.image_url} alt="Profile" className="w-28 h-28 object-cover rounded-full border-4 border-primary shadow" />
        ) : (
          <div className="w-28 h-28 rounded-full bg-gray-700 flex items-center justify-center text-4xl text-primary">?</div>
        )}
        <label className="mt-3 text-sm text-gray-300 font-medium cursor-pointer">
          <span className="flex items-center gap-2"><FaUpload /> Upload Image</span>
          <input
            type="file"
            accept="image/*"
            onChange={e => {
              const file = e.target.files[0];
              if (file) {
                if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
                  toast.error(`Image must be less than ${MAX_IMAGE_SIZE_MB}MB`);
                  return;
                }
                handleImageUpload(file);
              }
            }}
            className="hidden"
            disabled={imageUploading}
          />
        </label>
        {imageUploading && <div className="text-primary flex items-center gap-2 mt-2"><FaSpinner className="animate-spin" /> Uploading...</div>}
        {profileData.image_url && (
          <button type="button" onClick={() => setProfileData(prev => ({ ...prev, image_url: '' }))} className="mt-2 text-xs text-red-400 hover:text-red-300 flex items-center gap-1"><FaTimes /> Remove</button>
        )}
      </div>
      <div className="space-y-4">
        <input name="name" value={profileData.name || ''} onChange={handleProfileChange} placeholder="Full Name" required className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary" />
        <input name="subheading" value={profileData.subheading || ''} onChange={handleProfileChange} placeholder="Title (e.g. AI Engineer)" className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary" />
        <input name="tagline" value={profileData.tagline || ''} onChange={handleProfileChange} placeholder="Tagline (e.g. Building AI and web solutions.)" className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary" />
        <input name="button_text" value={profileData.button_text || ''} onChange={handleProfileChange} placeholder="Button Text (e.g. Contact Me)" className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary" />
        <input name="button_link" value={profileData.button_link || ''} onChange={handleProfileChange} placeholder="Button Link (e.g. /contact)" className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary" />
        <div className="flex items-center gap-2 mt-2">
          <label htmlFor="show_button" className="text-white">Show Button</label>
          <input
            id="show_button"
            name="show_button"
            type="checkbox"
            checked={profileData.show_button ?? true}
            onChange={e => {
              setProfileData(prev => ({ ...prev, show_button: e.target.checked }));
              setHasChanges && setHasChanges(true);
            }}
            className="w-5 h-5"
          />
        </div>
      </div>
      <div className="mt-6">
        <label className="block text-sm font-medium text-gray-300 mb-2">Roles (for AutoTyping)</label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={roleInput}
            onChange={e => setRoleInput(e.target.value)}
            placeholder="Add new role"
            className="flex-1 px-3 py-2 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddRole(); } }}
          />
          <button onClick={handleAddRole} className="bg-primary text-white px-4 py-2 rounded-lg flex items-center gap-1" type="button"><FaPlus /> Add</button>
        </div>
        <ul className="space-y-2">
          {(profileData.roles || []).map((role, idx) => (
            <li key={idx} className="flex items-center gap-2">
              <input
                type="text"
                value={role}
                onChange={e => handleEditRole(idx, e.target.value)}
                className="flex-1 px-3 py-2 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button onClick={() => handleRemoveRole(idx)} className="bg-red-600 text-white px-2 py-2 rounded-lg" type="button"><FaTrash /></button>
            </li>
          ))}
        </ul>
        {(profileData.roles || []).length === 0 && (
          <div className="text-gray-400 text-sm mt-2">No roles yet. Add your first role!</div>
        )}
      </div>
      <div className="mt-8 flex justify-end">
        {/* Remove Save Profile button here */}
      </div>
    </div>
  );
};

export default EditHero; 